const CONTENT_TYPES = new Set(["event", "promotion", "notice", "faq"]);
const DAY_IN_MS = 24 * 60 * 60 * 1000;

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");
}

function normalizeType(value) {
  const type = String(value || "").trim().toLowerCase();
  if (CONTENT_TYPES.has(type)) return type;
  if (["promo", "프로모션"].includes(type)) return "promotion";
  if (["공지", "announcement"].includes(type)) return "notice";
  return "";
}

function toPlainText(value) {
  return String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function parseDate(value, endOfDay = false) {
  if (!value) return null;

  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      endOfDay ? 23 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 999 : 0,
    );
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = parseDate(value);
  if (!date) return "";

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join(".");
}

function formatPeriod(publishedAt, expiresAt) {
  const start = formatDate(publishedAt);
  const end = formatDate(expiresAt);
  if (start && end) return `${start} ~ ${end}`;
  return end || start || "상시 진행";
}

function getEventStatus(expiresAt, now) {
  const expiresDate = parseDate(expiresAt, true);
  if (!expiresDate) return { status: "open", statusLabel: "진행중" };

  if (expiresDate.getTime() < now.getTime()) return { status: "closed", statusLabel: "종료" };

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiryDay = new Date(expiresDate.getFullYear(), expiresDate.getMonth(), expiresDate.getDate());
  const remainingDays = Math.round((expiryDay.getTime() - today.getTime()) / DAY_IN_MS);
  if (remainingDays <= 7) return { status: "ending", statusLabel: `D-${remainingDays}` };
  return { status: "open", statusLabel: "신청 가능" };
}

function getShortPeriod(expiresAt, status) {
  const expiresDate = parseDate(expiresAt);
  if (!expiresDate) return "상시 진행";

  const dateLabel = `${expiresDate.getMonth() + 1}월 ${expiresDate.getDate()}일`;
  return status === "closed" ? `${dateLabel} 종료` : `${dateLabel}까지`;
}

function getContentId(row, type, index) {
  const rowId = firstValue(row?.id, row?.content_id, row?.contentId, index);
  return `edrp-${type}-${String(rowId)}`;
}

function normalizeRow(row, index) {
  const type = normalizeType(firstValue(row?.type, row?.content_type, row?.contentType, row?.category));
  if (!type) return null;

  return {
    id: getContentId(row, type, index),
    sourceId: firstValue(row?.id, row?.content_id, row?.contentId, index),
    type,
    title: toPlainText(firstValue(row?.title, row?.subject, row?.post_title)),
    summary: toPlainText(firstValue(row?.summary, row?.description, row?.excerpt)),
    content: toPlainText(firstValue(row?.content, row?.contents, row?.post_contents)),
    imageUrl: String(firstValue(row?.imageUrl, row?.image_url, row?.thumbnail_url, row?.thumbnail) || "").trim(),
    publishedAt: firstValue(row?.publishedAt, row?.published_at, row?.start_at, row?.startDate),
    expiresAt: firstValue(row?.expiresAt, row?.expires_at, row?.end_at, row?.endDate),
    officeId: firstValue(row?.officeId, row?.office_id),
    themeId: firstValue(row?.themeId, row?.theme_id),
    cta: row?.cta && typeof row.cta === "object" ? row.cta : null,
  };
}

function mapEvent(row, { now, eventFallbackImage }) {
  const { status, statusLabel } = getEventStatus(row.expiresAt, now);
  const branch = row.officeId ? "MY STUDIO 내 지점" : "전 지점";
  const description = row.summary || row.content || "자세한 혜택과 이용 안내를 확인해보세요.";

  return {
    id: row.id,
    sourceId: row.sourceId,
    source: "edrp",
    readOnly: row.cta?.action !== "EVENT_APPLICATION",
    contentType: row.type,
    title: row.title || (row.type === "promotion" ? "프로모션 안내" : "이벤트 안내"),
    benefitTitle: row.title || row.summary || "이벤트 안내",
    description,
    content: row.content || row.summary,
    branch,
    branchName: row.officeId ? "내 지점" : "전 지점",
    officeId: row.officeId,
    themeId: row.themeId,
    period: formatPeriod(row.publishedAt, row.expiresAt),
    shortPeriod: getShortPeriod(row.expiresAt, status),
    publishedAt: row.publishedAt,
    expiresAt: row.expiresAt,
    image: row.imageUrl || eventFallbackImage,
    category: row.type,
    audience: row.officeId ? "해당 지점 고객 안내" : "전체 고객 안내",
    status,
    statusLabel,
    benefits: row.summary ? [row.summary] : [],
    notice: row.content || row.summary || "상세 내용은 스튜디오에 문의해주세요.",
    cta: row.cta,
  };
}

function mapNotice(row, { noticeFallbackImage }) {
  const date = formatDate(row.publishedAt);

  return {
    id: row.id,
    sourceId: row.sourceId,
    source: "edrp",
    readOnly: true,
    type: row.type === "faq" ? "faq" : "notice",
    badge: row.type === "faq" ? "FAQ" : "공지",
    title: row.title || (row.type === "faq" ? "자주 묻는 질문" : "공지사항"),
    summary: row.summary,
    content: row.content || row.summary,
    image: row.imageUrl || noticeFallbackImage,
    officeId: row.officeId,
    themeId: row.themeId,
    publishedAt: row.publishedAt,
    expiresAt: row.expiresAt,
    meta: `${row.officeId ? "내 지점" : "전체 지점"}${date ? ` · ${date}` : ""}`,
    route: `/tabs/home?screen=public-content&contentId=${encodeURIComponent(row.id)}`,
  };
}

export function mapPublicContentItems(items, {
  now = new Date(),
  eventFallbackImage = "",
  noticeFallbackImage = "",
} = {}) {
  const normalizedRows = (Array.isArray(items) ? items : [])
    .map(normalizeRow)
    .filter(Boolean);

  return {
    events: normalizedRows
      .filter((row) => row.type === "event" || row.type === "promotion")
      .map((row) => mapEvent(row, { now, eventFallbackImage })),
    notices: normalizedRows
      .filter((row) => row.type === "notice" || row.type === "faq")
      .map((row) => mapNotice(row, { noticeFallbackImage })),
  };
}

export function resolvePublicContentSources(mappedContent, {
  isAuthoritative = false,
  fallbackEvents = [],
  fallbackNotices = [],
} = {}) {
  const events = Array.isArray(mappedContent?.events) ? mappedContent.events : [];
  const notices = Array.isArray(mappedContent?.notices) ? mappedContent.notices : [];

  return {
    events: isAuthoritative ? events : fallbackEvents,
    notices: isAuthoritative ? notices : fallbackNotices,
  };
}

export { formatDate as formatPublicContentDate };
